import { Check as CheckIcon, Star as StarIcon, Business as BusinessIcon, ContentCopy as ContentIcon, Support as SupportIcon } from '@mui/icons-material';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface Plan {
  id: number;
  name: string;
  description: string;
  price: number;
  features: string;
  is_active: boolean;
}

interface PlanFeatures {
  monthly_limit: number;
  daily_limit: number;
  business_profiles_limit: number;
  platforms: string[];
  unlimited?: boolean;
  priority_support?: boolean;
}

interface PlanCardProps {
  plan: Plan;
  features: PlanFeatures;
  isCurrentPlan: boolean;
  isPopular: boolean;
  onUpgrade: (planId: number) => void;
  isUpgrading?: boolean;
}

const getPlanIcon = (planName: string) => {
  switch (planName.toLowerCase()) {
    case 'free':
      return <BusinessIcon color="primary" />;
    case 'starter':
      return <ContentIcon color="primary" />;
    case 'pro':
      return <StarIcon color="primary" />;
    case 'agency':
      return <BusinessIcon color="secondary" />;
    default:
      return <BusinessIcon color="primary" />;
  }
};


export function PlanCard({ plan, features, isCurrentPlan, isPopular, onUpgrade, isUpgrading = false }: PlanCardProps) {
  return (
    <Card className={cn(
      "relative h-full flex flex-col transition-all duration-200 hover:shadow-lg",
      isCurrentPlan && "ring-2 ring-primary",
      isPopular && "border-primary shadow-md"
    )}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <Badge className="bg-primary text-primary-foreground px-3 py-1">
            Most Popular
          </Badge>
        </div>
      )}
      
      {isCurrentPlan && (
        <div className="absolute -top-3 right-4 z-10">
          <Badge variant="secondary" className="px-3 py-1">
            Current Plan
          </Badge>
        </div>
      )}

      <CardHeader className="text-center pb-4">
        <div className="flex justify-center mb-2">
          {getPlanIcon(plan.name)}
        </div>
        <h3 className="text-xl font-semibold">{plan.name}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {plan.description}
        </p>
        <div className="mt-4">
          <span className="text-3xl font-bold">${plan.price}</span>
          <span className="text-muted-foreground">/month</span>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        <ul className="space-y-3 flex-1">
          <li className="flex items-start gap-3">
            <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <span className="text-sm">
              {features.monthly_limit === -1 ? 'Unlimited' : features.monthly_limit} content generations per month
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <span className="text-sm">
              {features.business_profiles_limit === -1 ? 'Unlimited' : features.business_profiles_limit} business profile{features.business_profiles_limit === -1 || features.business_profiles_limit > 1 ? 's' : ''}
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <span className="text-sm">
              {features.daily_limit === -1 ? 'Unlimited' : features.daily_limit} generations per day
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <span className="text-sm">
              {features.platforms.length === 1 && features.platforms[0] === 'all' ? 'All' : features.platforms.length} platform{features.platforms.length > 1 ? 's' : ''}: {features.platforms.join(', ')}
            </span>
          </li>
          {features.priority_support && (
            <li className="flex items-start gap-3">
              <SupportIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-sm">Priority support</span>
            </li>
          )}
        </ul>

        <div className="mt-6">
          <Button
            className={cn(
              "w-full",
              isCurrentPlan && "bg-muted text-muted-foreground cursor-not-allowed",
              isPopular && !isCurrentPlan && "bg-primary hover:bg-primary/90"
            )}
            disabled={isCurrentPlan || isUpgrading}
            onClick={() => onUpgrade(plan.id)}
          >
            {isCurrentPlan ? 'Current Plan' : `Upgrade to ${plan.name}`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
